/**
 * useLiveRefresh — keeps a screen current without a page refresh (Matt, 2026-10-27: "Make it so the flight
 * board updates in real time as much as reasonable so I don't have to refresh the page to see updates").
 *
 * Re-reads quietly:
 *   - every `everyMs` while the tab is visible (a hidden browser tab never polls),
 *   - the moment the tab or window comes back into view (at most once every 5 seconds),
 *   - on any of `events` (e.g. 'crew-index-changed' after a crew write anywhere in this browser),
 *   - on 'flight-board-refresh', which the Flight Board's own Refresh button fires.
 * Skips a beat while `pause()` says so (a picker or dialog open, a save in flight), while a mouse button or
 * pen is held down (never re-render under a drag), and while the last read is still running.
 *
 * Polling, not Supabase Realtime: the flight tables are closed to the browser's key (every read goes
 * through api/flights.js), and the board's own read joins Airtable too.
 */
import { useEffect, useRef } from 'react'

let pointerDown = false
if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', () => { pointerDown = true }, true)
  window.addEventListener('pointerup', () => { pointerDown = false }, true)
  window.addEventListener('pointercancel', () => { pointerDown = false }, true)
  window.addEventListener('blur', () => { pointerDown = false })
}

export default function useLiveRefresh(fn, { everyMs = 30000, events = [], pause = null, enabled = true } = {}) {
  const fnRef = useRef(fn); fnRef.current = fn
  const pauseRef = useRef(pause); pauseRef.current = pause
  const running = useRef(false)
  const last = useRef(Date.now())
  const evKey = events.join('|')

  useEffect(() => {
    if (!enabled) return undefined
    let alive = true
    const run = async (force) => {
      if (!alive || running.current) return
      if (!force && (document.hidden || pointerDown || (pauseRef.current && pauseRef.current()))) return
      running.current = true; last.current = Date.now()
      try { await fnRef.current() } catch { /* a missed beat; the next one tries again */ } finally { running.current = false }
    }
    const tick = () => run(false)
    const onShow = () => { if (!document.hidden && Date.now() - last.current > 5000) run(false) }
    const onEvent = () => setTimeout(() => run(true), 400)   // let the write land before re-reading
    const id = setInterval(tick, everyMs)
    document.addEventListener('visibilitychange', onShow)
    window.addEventListener('focus', onShow)
    const evs = [...evKey.split('|').filter(Boolean), 'flight-board-refresh']
    evs.forEach(e => window.addEventListener(e, onEvent))
    return () => {
      alive = false; clearInterval(id)
      document.removeEventListener('visibilitychange', onShow)
      window.removeEventListener('focus', onShow)
      evs.forEach(e => window.removeEventListener(e, onEvent))
    }
  }, [everyMs, evKey, enabled])
}
